import React from 'react'
// libs
import { useSelector } from 'react-redux'
import { useMutation, useQuery } from '@apollo/client/react'
// graphql
import { CHART_DATA_QUERY, CHART_QUERY } from 'graphql/chart/queries'
import { RISK_VIEW_FILTERS_QUERY } from 'graphql/risk/queries'
import { CHART_CREATE_MUTATION, CHART_UPDATE_MUTATION } from 'graphql/chart/mutations'
// types
import { Widget } from 'store/reducers/dashboards'
import {
  IChart,
  FilterTarget,
  IFiltersResponse,
  IChartCreateInput,
  IChartUpdateInput,
  DashboardItemType
} from 'types/graphql'
// store
import { riskTemplatesSelector } from 'store/reducers/riskTemplates'
// utils
import { omit } from 'lodash'
import { getChartDataInput } from 'utils/charts/config'
import {
  ChartFormValues,
  getChartFormValues,
  getChartConfigFromForm
} from 'components/Modals/WidgetSettingsModal/components/Form/components/ChartFields/utils'

// The chart a CHART tile shows: created on the tile's first save (chartCreate), edited after (chartUpdate)
export const useChartSettings = (widget?: Widget) => {
  const isChart = widget?.item.itemType === DashboardItemType.Chart
  const chartId = isChart ? widget?.item.settings?.chartId : undefined

  const riskTemplates = useSelector(riskTemplatesSelector)

  const {
    data: chartData,
    loading: isChartLoading,
    error: chartError
  } = useQuery<{ chart: IChart }>(CHART_QUERY, {
    variables: { id: chartId },
    skip: !chartId
  })

  const chart = chartId ? chartData?.chart : undefined
  const riskTemplateId = chart?.query?.riskTemplateId

  // Only to name the saved filter in its select
  const { data: filtersData, loading: isFiltersLoading } = useQuery<{ filters: IFiltersResponse }>(
    RISK_VIEW_FILTERS_QUERY,
    {
      variables: { filter: { targetId: riskTemplateId, target: FilterTarget.RiskTemplate, global: true } },
      skip: !riskTemplateId || !chart?.query?.filterId
    }
  )

  const [createChart] = useMutation<{ chartCreate: IChart }, { input: IChartCreateInput }>(CHART_CREATE_MUTATION)
  const [updateChart] = useMutation<{ chartUpdate: IChart }, { input: IChartUpdateInput }>(CHART_UPDATE_MUTATION)

  // A chart created by a save whose tile update then failed: the next save edits it, not creates one more
  const createdChartIdRef = React.useRef<string>(null)

  React.useEffect(() => {
    createdChartIdRef.current = null
  }, [widget?.item.id])

  const initialValues = React.useMemo(
    () =>
      isChart ? getChartFormValues({ chart, riskTemplates, filters: filtersData?.filters?.data || [] }) : undefined,
    [isChart, chart, riskTemplates, filtersData]
  )

  // Returns the id of the saved chart
  const saveChart = async (values: ChartFormValues): Promise<string> => {
    const { query } = getChartConfigFromForm(values)
    const input = {
      title: values.chartTitle?.trim(),
      // The server computes the query and keeps it apart
      query: getChartDataInput(query),
      // As the form holds it, not parsed here: the server fills the defaults, or refuses a wrong value with
      // CHART_CONFIG_INVALID and the paths of the problems, where parsing here would quietly reset it
      config: omit(values.chartConfig, 'query')
    }

    const savedChartId = chartId || createdChartIdRef.current

    if (savedChartId) {
      await updateChart({
        variables: { input: { id: savedChartId, ...input } },
        // A new query drops the snapshots on the server: the tile reads its numbers again
        refetchQueries: [{ query: CHART_DATA_QUERY, variables: { chartId: savedChartId } }]
      })
      return savedChartId
    }

    const response = await createChart({ variables: { input } })

    createdChartIdRef.current = response.data.chartCreate.id

    return createdChartIdRef.current
  }

  // The form opens on the saved chart, not on the defaults it would be reset from when the chart arrives
  const isLoading = isChartLoading || isFiltersLoading
  // Nor on the defaults when the saved chart failed to load: a save would overwrite it with them
  const loadError = chartId && !chart ? chartError : undefined

  return { isChart, isLoading, loadError, initialValues, saveChart }
}
